import { Conversation } from "@elevenlabs/client";
import { getBaseUrl, patientConciergeApiBase } from "@/lib/api-base";
import { getAuthToken } from "@/lib/auth/authService";
import { getDeviceId, getTenantKey } from "@/lib/room-context";
import { authHeaders } from "../management-api";

/** Present only when status returns HTTP 200. */
export type AgentConfig = {
  mode: "signed" | "none";
  agentId: string | null;
  wakePhrase: string;
  wakeWordOnnxPath: string | null;
};

export type RequestTypeItem = {
  code: string;
  title: string;
  iconKey: string | null;
  sortOrder: number;
};

export interface BasicResponse {
  isSuccess: boolean;
  errorMessage: string;
  message: string;
}

export type AgentSessionHandlers = {
  onConnect?: (conversationId: string) => void;
  onDisconnect?: () => void;
  onError?: (message: string) => void;
  onModeChange?: (mode: "speaking" | "listening") => void;
  onMessage?: (message: {
    message: string;
    source?: "user" | "ai";
    role?: string;
  }) => void;
  onVadScore?: (score: number) => void;
};

export type AgentSession = {
  end: () => Promise<void>;
  /** Injects text as if the patient had spoken it. */
  sendUserMessage: (text: string) => void;
  /** Non-spoken context for the agent (does not appear in the transcript). */
  sendContextualUpdate: (text: string) => void;
  /** Current mic loudness in the agent session, normalised to `0..1`. */
  getInputVolume: () => number;
  deviceId: string;
};

function voiceAgentUrl(path: string): string {
  const base = patientConciergeApiBase();
  if (base) {
    return `${base}/v1/patient-concierge/voice-agent/${path}`;
  }
  // Local Rsbuild proxy (see rsbuild.config.ts).
  return `/api/voice-agent/${path}`;
}

/**
 * Authorization for room/voice-agent calls. Prefer an MSI-injected
 * `localStorage.access_token`; fall back to the staff cookie JWT.
 */
function authHeadersLocalStorage(extra?: HeadersInit): HeadersInit {
  const token = localStorage.getItem("access_token") || getAuthToken();
  return {
    ...(extra ?? {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export { getDeviceId, getTenantKey } from "@/lib/room-context";

/** Returns null when status is not HTTP 200 (tenant unavailable / misconfigured). */
export async function fetchAgentConfig(options?: {
  deviceId?: string | null;
  tenantKey?: string | null;
  /** Staff cookie auth when true; otherwise localStorage token (room TV). */
  loggedIn?: boolean;
}): Promise<AgentConfig | null> {
  try {
    const deviceId = (options?.deviceId ?? getDeviceId()).trim();
    const tenantKey = (options?.tenantKey ?? getTenantKey()).trim();
    const loggedIn = options?.loggedIn === true;
    if (!tenantKey) {
      return null;
    }
    const params = new URLSearchParams();
    if (deviceId) params.set("deviceId", deviceId);

    const res = await fetch(voiceAgentUrl(`status?${params.toString()}`), {
      cache: "no-store",
      headers: loggedIn ? authHeaders() : authHeadersLocalStorage(),
    });
    if (!res.ok) {
      return null;
    }
    const body = (await res.json()) as {
      mode?: string;
      Mode?: string;
      agentId?: string | null;
      AgentId?: string | null;
      wakePhrase?: string | null;
      WakePhrase?: string | null;
      wakeWordOnnxPath?: string | null;
      WakeWordOnnxPath?: string | null;
    };
    const modeRaw = (body.mode ?? body.Mode ?? "none").trim().toLowerCase();
    const mode: AgentConfig["mode"] = modeRaw === "signed" ? "signed" : "none";
    return {
      mode,
      agentId: body.agentId ?? body.AgentId ?? null,
      wakePhrase: (body.wakePhrase ?? body.WakePhrase)?.trim() || "",
      wakeWordOnnxPath:
        (body.wakeWordOnnxPath ?? body.WakeWordOnnxPath)?.trim() || null,
    };
  } catch {
    return null;
  }
}

export async function awakeDevice(
  deviceId: string = getDeviceId(),
): Promise<boolean> {
  if (deviceId.length === 0) return false;
  const baseUrl = patientConciergeApiBase();
  if (!baseUrl) return false;

  const req = {
    deviceId,
  };

  try {
    const res = await fetch(
      `${baseUrl.replace(/\/$/, "")}/v1/patient-concierge/device/tv-workflow/start`,
      {
        method: "POST",
        headers: authHeadersLocalStorage({
          "Content-Type": "application/json",
        }),
        cache: "no-store",
        body: JSON.stringify(req),
      },
    );
    if (!res.ok) return false;

    const body = (await res.json()) as BasicResponse;
    return body.isSuccess === true;
  } catch {
    return false;
  }
}

export async function stopDevice(
  deviceId: string = getDeviceId(),
): Promise<boolean> {
  if (deviceId.length === 0) return false;
  const baseUrl = patientConciergeApiBase();
  if (!baseUrl) return false;

  const req = {
    deviceId,
  };

  try {
    const res = await fetch(
      `${baseUrl.replace(/\/$/, "")}/v1/patient-concierge/device/tv-workflow/stop`,
      {
        method: "POST",
        headers: authHeadersLocalStorage({
          "Content-Type": "application/json",
        }),
        cache: "no-store",
        body: JSON.stringify(req),
      },
    );
    if (!res.ok) return false;

    const body = (await res.json()) as BasicResponse;
    return body.isSuccess === true;
  } catch {
    return false;
  }
}

export async function fetchRequestTypes(
  tenantKey: string = getTenantKey(),
): Promise<RequestTypeItem[]> {
  const key = tenantKey.trim();
  if (!key) return [];
  try {
    const qs = `?tenantKey=${encodeURIComponent(key)}`;
    const res = await fetch(voiceAgentUrl(`request-types${qs}`), {
      cache: "no-store",
      headers: authHeadersLocalStorage(),
    });
    if (!res.ok) return [];
    const body = (await res.json()) as {
      items?: Array<{
        code?: string;
        Code?: string;
        title?: string;
        Title?: string;
        iconKey?: string | null;
        IconKey?: string | null;
        sortOrder?: number;
        SortOrder?: number;
      }>;
      Items?: Array<{
        code?: string;
        Code?: string;
        title?: string;
        Title?: string;
        iconKey?: string | null;
        IconKey?: string | null;
        sortOrder?: number;
        SortOrder?: number;
      }>;
    };
    const rows = body.items ?? body.Items ?? [];
    return rows
      .map((r) => ({
        code: (r.code ?? r.Code ?? "").trim(),
        title: (r.title ?? r.Title ?? "").trim(),
        iconKey: (r.iconKey ?? r.IconKey)?.trim() || null,
        sortOrder: r.sortOrder ?? r.SortOrder ?? 0,
      }))
      .filter((r) => r.code && r.title)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
      );
  } catch {
    return [];
  }
}

type StartSessionResult = {
  signedUrl: string;
  tenantKey: string;
  deviceId: string;
  dynamicVariables: Record<string, string>;
};

async function startVoiceSession(
  deviceId: string,
  wakePhrase?: string | null,
): Promise<StartSessionResult> {
  const tenantKey = getTenantKey().trim();
  if (!tenantKey) {
    throw new Error("tenantKey is required.");
  }
  const res = await fetch(voiceAgentUrl("session"), {
    method: "POST",
    headers: authHeadersLocalStorage({ "Content-Type": "application/json" }),
    cache: "no-store",
    body: JSON.stringify({
      deviceId,
      tenantKey,
      ...(wakePhrase?.trim() ? { wakePhrase: wakePhrase.trim() } : {}),
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: string;
      Message?: string;
      error?: string;
    } | null;
    throw new Error(
      body?.error ??
        body?.message ??
        body?.Message ??
        `Start session failed (${res.status})`,
    );
  }

  const body = (await res.json()) as {
    signedUrl?: string;
    SignedUrl?: string;
    tenantKey?: string;
    TenantKey?: string;
    deviceId?: string;
    DeviceId?: string;
    dynamicVariables?: Record<string, string>;
    DynamicVariables?: Record<string, string>;
  };

  const signedUrl = body.signedUrl ?? body.SignedUrl;
  if (!signedUrl) {
    throw new Error("Session response missing signedUrl");
  }

  const dynamicVariables = body.dynamicVariables ??
    body.DynamicVariables ?? {
      device_id: body.deviceId ?? body.DeviceId ?? deviceId,
      tenant_key: body.tenantKey ?? body.TenantKey ?? tenantKey,
    };

  return {
    signedUrl,
    tenantKey: body.tenantKey ?? body.TenantKey ?? tenantKey,
    deviceId: body.deviceId ?? body.DeviceId ?? deviceId,
    dynamicVariables,
  };
}

/**
 * Notify the backend that the voice agent session has ended for this device.
 * Idempotent when no Active session exists. Best-effort — failures are ignored.
 */
async function endVoiceSession(
  deviceId: string,
  reason: string = "client_close",
): Promise<void> {
  const id = deviceId.trim();
  if (!id) return;
  try {
    await fetch(voiceAgentUrl("session/end"), {
      method: "POST",
      headers: authHeadersLocalStorage({ "Content-Type": "application/json" }),
      cache: "no-store",
      body: JSON.stringify({ deviceId: id, reason }),
    });
  } catch {
    // Best-effort: local teardown should proceed even if the API is unreachable.
  }
}

/**
 * Start a voice agent session; passes device_id + tenant_key as ElevenLabs dynamic variables
 * so webhook tool calls can resolve room context and the active session.
 */
export async function startAgentSession(
  config: AgentConfig,
  handlers: AgentSessionHandlers = {},
  deviceId: string = getDeviceId(),
): Promise<AgentSession> {
  if (config.mode !== "signed") {
    throw new Error("Voice agent is not ready for this device.");
  }

  const session = await startVoiceSession(deviceId, config.wakePhrase);

  // Call session/end once when the agent actually disconnects — not when the
  // patient submits another request while the websocket stays open.
  let backendEndNotified = false;
  const notifyBackendSessionEnded = () => {
    if (backendEndNotified) return;
    backendEndNotified = true;
    void endVoiceSession(session.deviceId, "client_close");
  };

  try {
    const conversation = await Conversation.startSession({
      connectionType: "websocket",
      signedUrl: session.signedUrl,
      dynamicVariables: session.dynamicVariables,
      onConnect: ({ conversationId }: { conversationId: string }) => {
        handlers.onConnect?.(conversationId);
      },
      onDisconnect: () => {
        notifyBackendSessionEnded();
        handlers.onDisconnect?.();
      },
      onError: (message: string) => {
        handlers.onError?.(message);
      },
      onModeChange: ({ mode }: { mode: "speaking" | "listening" }) => {
        handlers.onModeChange?.(mode);
      },
      onMessage: (message: {
        message: string;
        source?: "user" | "ai";
        role?: string;
      }) => {
        handlers.onMessage?.(message);
      },
      onVadScore: ({ vadScore }: { vadScore: number }) => {
        handlers.onVadScore?.(vadScore);
      },
    });

    return {
      deviceId: session.deviceId,
      end: async () => {
        notifyBackendSessionEnded();
        await conversation.endSession();
      },
      sendUserMessage: (text: string) => {
        conversation.sendUserMessage(text);
      },
      sendContextualUpdate: (text: string) => {
        conversation.sendContextualUpdate(text);
      },
      getInputVolume: () => conversation.getInputVolume(),
    };
  } catch (err) {
    // Backend Active row was created above; clean it up if the websocket
    // never connected so retries don't leak sessions.
    notifyBackendSessionEnded();
    throw err;
  }
}
